/**
 * BrandContext — active brand_code + integrated-brand list (M9).
 *
 * The active brand is determined in this order (per spec §2.4.6):
 *   1. URL prefix (`/{brand}/...`) when one of the routes matches
 *   2. `pe_brand` cookie
 *   3. `DEFAULT_BRAND_CODE` (compile-time fallback — `polestar`)
 *
 * The integrated-brand list is fetched once on mount via `GET /api/brands`
 * and cached for `BRAND_REGISTRY_CACHE_TTL_S` seconds (default 300).
 * The provider exposes `setActiveBrand` so the BrandSelector can update
 * the cookie and trigger a re-render; route navigation is the caller's
 * responsibility.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { type BrandEntry, getBrands } from "../api/client";

export const DEFAULT_BRAND_CODE = "polestar";
export const BRAND_COOKIE_NAME = "pe_brand";
const BRAND_REGISTRY_CACHE_TTL_MS = 5 * 60 * 1000; // 300 s

interface BrandContextValue {
  brands: BrandEntry[];
  activeBrand: string;
  setActiveBrand: (brand: string) => void;
  isLoading: boolean;
  error: string | null;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export function readBrandCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${BRAND_COOKIE_NAME}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.split("=", 2)[1] ?? "");
  return value || null;
}

export function writeBrandCookie(brand: string): void {
  if (typeof document === "undefined") return;
  // 1 year, root path, lax samesite — non-sensitive UI preference.
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${BRAND_COOKIE_NAME}=${encodeURIComponent(brand)}; max-age=${oneYear}; path=/; samesite=lax`;
}

interface BrandProviderProps {
  children: ReactNode;
  // Override for tests so they don't hit /api/brands; if provided, the fetch
  // is skipped entirely.
  initialBrands?: BrandEntry[];
  // Override the resolution of the active brand (URL prefix). The
  // App-level wrapper passes the {brand} URL param when present.
  urlBrand?: string;
}

export function BrandProvider({
  children,
  initialBrands,
  urlBrand,
}: BrandProviderProps) {
  const [brands, setBrands] = useState<BrandEntry[]>(initialBrands ?? []);
  const [isLoading, setIsLoading] = useState(initialBrands === undefined);
  const [error, setError] = useState<string | null>(null);
  const [cookieBrand, setCookieBrand] = useState<string | null>(() =>
    readBrandCookie(),
  );

  useEffect(() => {
    if (initialBrands !== undefined) return;
    let cancelled = false;
    const cached = readCachedBrands();
    if (cached !== null) {
      setBrands(cached);
      setIsLoading(false);
      return;
    }
    getBrands()
      .then((resp) => {
        if (cancelled) return;
        setBrands(resp.brands);
        cacheBrands(resp.brands);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialBrands]);

  const setActiveBrand = useCallback((brand: string) => {
    writeBrandCookie(brand);
    setCookieBrand(brand);
  }, []);

  const activeBrand = useMemo(() => {
    if (urlBrand && isBrandValid(urlBrand, brands)) return urlBrand;
    if (cookieBrand && isBrandValid(cookieBrand, brands)) return cookieBrand;
    return DEFAULT_BRAND_CODE;
  }, [urlBrand, cookieBrand, brands]);

  const value: BrandContextValue = {
    brands,
    activeBrand,
    setActiveBrand,
    isLoading,
    error,
  };

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
}

export function useBrandContext(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (ctx !== undefined) return ctx;
  // Default fallback for components rendered outside a BrandProvider —
  // typically existing pre-M9 unit tests that mount a single page in
  // isolation. Active brand is the default; brand list is empty so
  // BrandSelector hides itself.
  return {
    brands: [],
    activeBrand: DEFAULT_BRAND_CODE,
    setActiveBrand: () => {},
    isLoading: false,
    error: null,
  };
}

function isBrandValid(brand: string, brands: BrandEntry[]): boolean {
  // When the brand list hasn't loaded yet, accept any value — we'll
  // validate again once the list arrives.
  if (brands.length === 0) return true;
  return brands.some((b) => b.brand_code === brand);
}

// In-memory cache so the brand list isn't re-fetched on every page mount.
let _cache: { brands: BrandEntry[]; fetched_at: number } | null = null;

function readCachedBrands(): BrandEntry[] | null {
  if (_cache === null) return null;
  if (Date.now() - _cache.fetched_at > BRAND_REGISTRY_CACHE_TTL_MS) {
    _cache = null;
    return null;
  }
  return _cache.brands;
}

function cacheBrands(brands: BrandEntry[]): void {
  _cache = { brands, fetched_at: Date.now() };
}

// Test seam: clear the in-memory cache between tests so each one starts fresh.
export function _resetBrandCache(): void {
  _cache = null;
}
